import type {
  AdapterDefinition,
  AdapterRegistry,
  GlobalCrawlConfigInput,
  SuburbInput,
} from "./types.ts";
import { buildQueryPaginatedUrl } from "./utils.ts";

export function createAdapterRegistry(
  definitions: readonly AdapterDefinition<any>[],
): AdapterRegistry {
  const adapters = new Map(definitions.map((definition) => [definition.meta.slug, definition]));

  const getAdapter = (slug: string) => adapters.get(slug);
  const getAllAdapters = () => Array.from(adapters.values());
  const hasAdapter = (slug: string) => adapters.has(slug);
  const findAdapterForUrl = (url: string) =>
    getAllAdapters().find((definition) => definition.meta.urlPattern.test(url));

  const getDefinitionOrThrow = (slug: string): AdapterDefinition<any> => {
    const definition = getAdapter(slug);
    if (!definition) {
      throw new Error(`Unknown adapter: ${slug}`);
    }
    return definition;
  };

  const resolve = (slug: string, config?: GlobalCrawlConfigInput) => {
    const definition = getDefinitionOrThrow(slug);
    return {
      definition,
      resolvedConfig: definition.resolveConfig(config),
    };
  };

  const getDefaultConfig = (slug: string): GlobalCrawlConfigInput => {
    const definition = getDefinitionOrThrow(slug);
    return { ...definition.defaultConfig };
  };

  const buildSearchUrl = (slug: string, suburb: SuburbInput, config?: GlobalCrawlConfigInput) => {
    if (config?.manualUrl?.trim()) {
      return config.manualUrl.trim();
    }
    const { definition, resolvedConfig } = resolve(slug, config);
    return definition.buildSearchUrl(suburb, resolvedConfig);
  };

  const buildPaginatedUrl = (
    slug: string,
    searchUrl: string,
    page: number,
    config?: GlobalCrawlConfigInput,
  ) => {
    const { definition, resolvedConfig } = resolve(slug, config);
    if (definition.buildPaginatedUrl) {
      return definition.buildPaginatedUrl(searchUrl, page, resolvedConfig);
    }
    return buildQueryPaginatedUrl(searchUrl, page);
  };

  return {
    getAdapter,
    getAllAdapters,
    hasAdapter,
    findAdapterForUrl,
    getDefaultConfig,
    buildSearchUrl,
    buildPaginatedUrl,
  };
}
